import React, { useRef, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, CircularProgress, IconButton } from '@mui/material';
import { CreateProjectDTO } from '../project.type';
import { ProjectInfo } from './ProjectInfo'; // Import your ProjectInfo component
import { ClientAndBorrowerInfo } from './ClientAndBorrowerInfo'; // Import your ClientAndBorrowerInfo component
import { OtherProjectInfo } from './OtherProjectInfo'; // Import your OtherInfo component
import { createProject } from './project-setup-wizard.api';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'common/constants/urls';
import { toast } from 'react-toastify';
import { VerticalLinearStepper } from './CustomStepper';
import ReviewAndSubmit from './ReviewAndSubmit';
import { Button } from 'components/index';
import CloseIcon from '@mui/icons-material/Close';

const initialProjectData: CreateProjectDTO = {
  projectInfo: {
    projectNumber: '',
    projectTitle: '',
    projectAddress: {
      street: '',
      city: '',
      state: undefined,
      postalCode: undefined,
    },
  },
  client: {
    name: '',
    address: {
      street: '',
      city: '',
      state: undefined,
      postalCode: undefined,
    },
    company: undefined,
    loanOfficerName: undefined,
  },
  borrower: {
    name: '',
    address: {
      street: '',
      city: '',
      state: undefined,
      postalCode: undefined,
    },
    company: undefined,
    loanOfficerName: undefined,
  },
  otherInfo: {
    contractor: null,
    loanOfficerName: null,
    observer: null,
    monitoringFee: null,
    monitoringType: null,
    planOrCostFee: null,
    planOrCostInvDate: null,
    siteArea: null,
    storyCount: null,
    buildingCount: null,
    unitCount: null,
    elevatorCount: null,
    otherCostFactor: null,
    grossBuildingArea: null,
    netArea: null,
    planOrCostReportDate: null,
    commencementDate: null,
    originalCompletionDateEstimated: null,
    revisedCompletionDate: null,
    bankLoanExpirationDate: null,
    constructionType: null,
    electrical: null,
    exteriorWalls: null,
    foundation: null,
    hVACScope: null,
    hVACType: null,
    plumbing: null,
    projectType: null,
    roof: null,
    superStructure: null,
    uniqueCostFactors1: null,
    uniqueCostFactors2: null,
    uniqueCostFactors3: null,
    windows: null,
    reviewingSubcontractorLienWaivers: null,
    reviewingConstructionMeetingMinutes: null,
    reviewingPotentialChangeOrders: null,
    reviewingPlanOrCost: null,
    networkerName: null,
    networkerStatus: null,
    corporation: null,
  },
};

interface ProjectWizardProps {
  open: boolean;
  onClose: () => void;
}

const steps = ['Project Info', 'Client and Borrower Info', 'Other Info', 'Review & Create'];

// Define the types for the methods of each step component
interface ProjectInfoFormMethods {
  submitForm: () => Promise<void>;
}

interface ClientAndBorrowerInfoFormMethods {
  submitForm: () => Promise<void>;
}

interface OtherProjectInfoFormMethods {
  submitForm: () => Promise<void>;
}

export const ProjectWizard = ({ open, onClose }: ProjectWizardProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [projectData, setProjectData] = useState<CreateProjectDTO>(initialProjectData);
  const projectInfoRef = useRef<ProjectInfoFormMethods>(null);
  const clientAndBorrowerInfoRef = useRef<ClientAndBorrowerInfoFormMethods>(null);
  const otherProjectInfoRef = useRef<OtherProjectInfoFormMethods>(null);
  const navigate = useNavigate();
  const [isCurrentFormValid, setIsCurrentFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleUpdate = (updatedPart: Partial<CreateProjectDTO>) => {
    setProjectData({ ...projectData, ...updatedPart });
  };

  const handleNextStep = async (skipToReview = false) => {
    if (currentStep === 0) {
      await projectInfoRef.current?.submitForm();
    } else if (currentStep === 1) {
      await clientAndBorrowerInfoRef.current?.submitForm();
    } else if (currentStep === 2) {
      await otherProjectInfoRef.current?.submitForm();
    }
    if (skipToReview === true && currentStep < steps.length - 1) {
      setCurrentStep(steps.length - 1);
    } else if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const response = await createProject(projectData);
      navigate(`${ROUTES.PROJECTS}/${response.projectId}`);
      onClose();
      toast.success('Project created successfully!');
    } catch (error: any) {
      if (error.response && error.response?.data && error.response?.data?.errors) {
        const errorMessage = Array.isArray(error.response.data.errors) ? error.response.data.errors.join(', ') : error.response.data.errors;
        toast.error(errorMessage);
      } else {
        const errorMessage = error?.response?.data?.errorMessage || 'An unexpected error occurred.';
        toast.error(errorMessage);
      }
    }
    setIsLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='lg' PaperProps={{ style: { width: '1000px', height: '700px' } }}>
      <div style={{ display: 'flex', height: '100%' }}>
        {' '}
        {/* Stepper container */}
        <div style={{ flex: '0 0 250px', height: '100%', overflow: 'auto', backgroundColor: '#015b70' }}>
          <VerticalLinearStepper
            activeStep={currentStep}
            handleNext={() => setCurrentStep(currentStep + 1)}
            handleBack={() => setCurrentStep(currentStep - 1)}
            handleReset={() => setCurrentStep(0)}
            steps={[
              { label: 'Step 1 - Project Information' },
              { label: 'Step 2 - Client and Borrower Information' },
              { label: 'Step 3 - Other Information' },
              { label: 'Step 4 - Review & Create' },
            ]}
          />
        </div>
        {/* Content container */}
        <div style={{ flex: '1', overflow: 'auto', display: 'flex', flexDirection: 'column', height: '100%' }}>
          <DialogTitle>
            <IconButton
              aria-label='close'
              onClick={onClose}
              style={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
            Project Setup Wizard
          </DialogTitle>
          <DialogContent style={{ overflowY: 'auto' }}>
            {currentStep === 0 && (
              <ProjectInfo
                ref={projectInfoRef}
                projectData={projectData.projectInfo}
                onUpdate={updatedInfo => handleUpdate({ projectInfo: updatedInfo })}
                onValidationChange={isValid => setIsCurrentFormValid(isValid)}
              />
            )}
            {currentStep === 1 && (
              <ClientAndBorrowerInfo
                ref={clientAndBorrowerInfoRef}
                ClientInfo={projectData.client}
                BorrowerInfo={projectData.borrower}
                onUpdate={(updatedClient, updatedBorrower) => handleUpdate({ client: updatedClient, borrower: updatedBorrower })}
                onValidationChange={isValid => setIsCurrentFormValid(isValid)}
              />
            )}
            {currentStep === 2 && (
              <OtherProjectInfo
                ref={otherProjectInfoRef}
                otherInfo={projectData.otherInfo}
                onUpdate={updatedOtherInfo => handleUpdate({ otherInfo: updatedOtherInfo })}
              />
            )}
            {currentStep === 3 && <ReviewAndSubmit projectData={projectData} onSubmit={handleSubmit} />}
            {/* Additional steps if any */}
          </DialogContent>
          <DialogActions style={{ marginTop: 'auto' }}>
            {currentStep > 0 && (
              <Button
                onClick={() => setCurrentStep(currentStep - 1)}
                variant='outlined'
                disabled={isLoading || (currentStep == 1 && !isCurrentFormValid)}
              >
                Previous
              </Button>
            )}
            {currentStep < steps.length - 1 && (
              <Button
                onClick={() => handleNextStep(true)}
                variant='outlined'
                disabled={(currentStep === 0 || currentStep == 1) && !isCurrentFormValid}
              >
                Review & Create
              </Button>
            )}
            {currentStep < steps.length - 1 && (
              <Button
                onClick={handleNextStep}
                variant='contained'
                disabled={(currentStep === 0 || currentStep == 1) && !isCurrentFormValid}
              >
                Next
              </Button>
            )}
            {currentStep === steps.length - 1 && (
              <Button
                onClick={handleSubmit}
                variant='contained'
                disabled={isLoading} // Disable the button when loading
                startIcon={isLoading && <CircularProgress size={24} color='inherit' />}
              >
                Create Project
              </Button>
            )}
          </DialogActions>
        </div>
      </div>
    </Dialog>
  );
};
